import { useContext, useEffect, useRef, useState } from 'react';
import { Text } from '@fluentui/react';
import { Field } from '@fluentui/react-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem, FIELD_LABEL_LAYOUT } from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import AppContext from '../AppContext';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createPricingItem,
  getPricingItem,
  getTimeServices,
  updatePricingItem,
} from '../../services/pricingService';
import { pricingItemsPath } from './pricingSettings';
import {
  CALCULATION_METHODS,
  CHARGE_ROLES,
  PERCENTAGE_BASE_ELIGIBLE_HINT,
  PERCENTAGE_BASES,
  PERCENTAGE_BASIS_HINT,
  pricingItemBody,
} from './pricingItemBody';

const PricingItemForm = () => {
  const isSaved = useRef(false);
  const [percentage, setPercentage] = useState(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const itemId = Number.parseInt(query.get('id') ?? '');
  const isEdit = !Number.isNaN(itemId) && itemId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const context = useContext(AppContext);
  const title = isEdit ? 'Edit pricing item' : 'Add pricing item';
  const [timeServiceOptions, setTimeServiceOptions] = useState<
    { key: string; value: string }[]
  >([]);

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    getTimeServices(abortController, 500, 1)
      .then((page) => {
        setTimeServiceOptions(
          [...page.items]
            .filter((service) => service.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((service) => ({
              key: String(service.id),
              value: service.name || service.code,
            }))
        );
      })
      .catch((error: { message?: string }) => {
        if (abortController.signal.aborted) return;
        setErrorMessage(error.message ?? 'Failed to load time services.');
      });
    return () => abortController.abort();
  }, [setErrorMessage]);

  const save = async (
    abortController: AbortController,
    item: Record<string, unknown>,
    id?: number
  ) => {
    const result = pricingItemBody(item);
    if (!result.ok) {
      setErrorMessage(result.error);
      throw new Error(result.error);
    }
    try {
      if (id == null) {
        await createPricingItem(abortController, result.body);
        setSuccessMessage(`Pricing item ${result.body.code} created.`);
      } else {
        await updatePricingItem(abortController, id, result.body);
        setSuccessMessage(`Pricing item ${result.body.code} updated.`);
      }
      navigate(pricingItemsPath);
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(error, 'Failed to save pricing item.')
      );
      throw error;
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{title}</Text>
      <EditItem
        getAction={async (abortController, id) => {
          const item = await getPricingItem(abortController, id);
          setPercentage(item.calculationMethod === 'Percentage');
          return item;
        }}
        afterChange={(item) =>
          setPercentage(item.calculationMethod === 'Percentage')
        }
        renderAfterField={(name) =>
          name === 'calculationMethod' && percentage ? (
            <Field
              label="Charge role"
              orientation="horizontal"
              style={FIELD_LABEL_LAYOUT}
            >
              <span style={{ alignSelf: 'center' }}>Additional charge</span>
            </Field>
          ) : null
        }
        newAction={(abortController, item) => save(abortController, item)}
        editAction={(abortController, id, item) =>
          save(abortController, item, id)
        }
        isSaved={isSaved}
        back={pricingItemsPath}
        confirmOnCancel={false}
        onCancel={() => navigate(pricingItemsPath)}
        defaultValues={
          isEdit
            ? undefined
            : JSON.stringify({ isRequired: true, quantityType: 'Fixed' })
        }
        metadata={{
          fields: [
            {
              name: 'code',
              fieldType: 'String',
              label: 'Code',
              mandatory: true,
              readonly: isEdit,
            },
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
              mandatory: true,
            },
            { name: 'description', fieldType: 'String', label: 'Description' },
            {
              name: 'type',
              fieldType: 'Lookup',
              label: 'Type',
              lookupList: 'PricingItemTypes',
              mandatory: true,
            },
            {
              name: 'timeServiceId',
              fieldType: 'Lookup',
              label: 'Time service',
              lookupList: 'TimeServices',
              effect: 'Hide',
            },
            {
              name: 'calculationMethod',
              fieldType: 'Lookup',
              label: 'Calculation method',
              lookupList: 'CalculationMethods',
              mandatory: true,
            },
            {
              name: 'chargeRole',
              fieldType: 'Lookup',
              label: 'Charge role',
              lookupList: 'ChargeRoles',
              mandatory: !percentage,
              effect: percentage ? 'Hide' : undefined,
            },
            {
              name: 'percentageBasis',
              fieldType: 'Lookup',
              label: 'Percentage basis',
              hint: PERCENTAGE_BASIS_HINT,
              lookupList: 'PercentageBases',
              mandatory: true,
              effect: 'Hide',
            },
            {
              name: 'quantityType',
              fieldType: 'Lookup',
              label: 'Quantity type',
              lookupList: 'QuantityTypes',
              mandatory: true,
            },
            {
              name: 'isPercentageBaseEligible',
              fieldType: 'Checkbox',
              label: 'Eligible for percentage base',
              hint: PERCENTAGE_BASE_ELIGIBLE_HINT,
              effect: 'Hide',
            },
            {
              name: 'exclusivityGroup',
              fieldType: 'String',
              label: 'Exclusivity group',
            },
            { name: 'isRequired', fieldType: 'Checkbox', label: 'Required' },
          ],
          lookups: [
            {
              name: 'PricingItemTypes',
              values: [
                { key: 'Service', value: 'Service' },
                { key: 'AddOn', value: 'AddOn' },
              ],
            },
            {
              name: 'QuantityTypes',
              values: [
                { key: 'Fixed', value: 'Fixed' },
                { key: 'AssetCount', value: 'AssetCount' },
              ],
            },
            { name: 'CalculationMethods', values: [...CALCULATION_METHODS] },
            { name: 'ChargeRoles', values: [...CHARGE_ROLES] },
            { name: 'PercentageBases', values: [...PERCENTAGE_BASES] },
            {
              name: 'TimeServices',
              values: [{ key: 'none', value: 'None' }, ...timeServiceOptions],
            },
          ],
          validations: [
            {
              field: 'code',
              rules: [{ rule: 'NE', errorMessage: 'Code is required.' }],
            },
            {
              field: 'name',
              rules: [{ rule: 'NE', errorMessage: 'Name is required.' }],
            },
            {
              field: 'type',
              rules: [{ rule: 'NE', errorMessage: 'Type is required.' }],
            },
            {
              field: 'quantityType',
              rules: [
                { rule: 'NE', errorMessage: 'Quantity type is required.' },
              ],
            },
            {
              field: 'calculationMethod',
              rules: [
                {
                  rule: 'NE',
                  errorMessage: 'Calculation method is required.',
                },
              ],
            },
            {
              field: 'chargeRole',
              rules: percentage
                ? []
                : [{ rule: 'NE', errorMessage: 'Charge role is required.' }],
            },
            {
              field: 'exclusivityGroup',
              rules: [
                {
                  rule: 'NGT30',
                  errorMessage:
                    'Exclusivity group must be 30 characters or less.',
                },
              ],
            },
          ],
          interactions: [
            {
              name: 'showTimeService',
              description: 'Time service applies to service pricing items.',
              condition: {
                combinator: 'And',
                not: false,
                rules: [{ field: 'type', operator: '=', value: 'Service' }],
              },
              effects: [{ fields: ['timeServiceId'], effectType: 'Show' }],
            },
            {
              name: 'showPercentageBasis',
              description:
                'Percentage items need a basis and always use a fixed quantity.',
              condition: {
                combinator: 'And',
                not: false,
                rules: [
                  {
                    field: 'calculationMethod',
                    operator: '=',
                    value: 'Percentage',
                  },
                ],
              },
              effects: [
                { fields: ['percentageBasis'], effectType: 'Show' },
                { fields: ['quantityType'], effectType: 'Hide' },
              ],
            },
            {
              name: 'showPercentageBaseEligible',
              description:
                'Only unit-rate additional charges can join a percentage base.',
              condition: {
                combinator: 'And',
                not: false,
                rules: [
                  {
                    field: 'calculationMethod',
                    operator: '=',
                    value: 'UnitRate',
                  },
                  {
                    field: 'chargeRole',
                    operator: '=',
                    value: 'AdditionalCharge',
                  },
                ],
              },
              effects: [
                { fields: ['isPercentageBaseEligible'], effectType: 'Show' },
              ],
            },
          ],
        }}
      />
    </div>
  );
};

export default PricingItemForm;
